import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Search, Sparkles, Zap, TrendingUp, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import slugify from '../utils/slugify';
import { Category } from '../data/types';

interface HomeHeaderProps {
  categories: string[];
  toolCategories: Category[];
}

type Suggestion = {
  label: string;
  type: 'category' | 'tool';
  category?: string;
};

// Memoized constants to prevent re-calculation
const QUICK_TAGS = ['AI Writing', 'Image Generation', 'Video Editing', 'Marketing', 'Chatbots', 'Productivity'];

const HomeHeader: React.FC<HomeHeaderProps> = ({ toolCategories }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isFocused, setIsFocused] = useState(false);
  const [shouldShowBackground, setShouldShowBackground] = useState(false);
  const navigate = useNavigate();
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null); // FIXED TYPE
  const inputRef = useRef<HTMLInputElement>(null);

  // Memoize expensive calculations
  const { totalTools, totalCategories, toolMap } = useMemo(() => {
    const totalTools = toolCategories.reduce((sum, cat) => sum + cat.tools.length, 0);
    const totalCategories = toolCategories.length;
    
    // Create a map for quick lookup
    const toolMap = toolCategories.reduce((acc, category) => {
      category.tools.forEach(tool => {
        acc[tool.title.toLowerCase()] = category.title;
      });
      return acc;
    }, {} as Record<string, string>);
    
    return { totalTools, totalCategories, toolMap };
  }, [toolCategories]);

  // SEO description memoized
  const seoDescription = useMemo(() => 
    `Explore our comprehensive directory featuring ${totalTools}+ carefully curated AI tools across ${totalCategories}+ specialized categories including AI writing assistants, image generators, video editing tools, chatbots, productivity apps, and marketing solutions. Find the perfect artificial intelligence tool to enhance your workflow, boost creativity, and increase productivity.`,
    [totalTools, totalCategories]
  );

  // Lazy load background after initial render
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);


  // Debounced search handler
  const handleSearchChange = useCallback((value: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      if (!value.trim()) {
        setSuggestions([]);
        return;
      }

      const lowerValue = value.toLowerCase();
      
      // Use cached calculations
      const categoryMatches = toolCategories
        .filter(cat => cat.title.toLowerCase().includes(lowerValue))
        .slice(0, 3)
        .map(cat => ({ label: cat.title, type: 'category' as const }));

      const toolMatches: Suggestion[] = [];
      
      // Limit search to first 5 categories for performance
      for (let i = 0; i < Math.min(5, toolCategories.length); i++) {
        const category = toolCategories[i];
        for (let j = 0; j < category.tools.length && toolMatches.length < 3; j++) {
          const tool = category.tools[j];
          if (tool.title.toLowerCase().includes(lowerValue)) {
            toolMatches.push({
              label: tool.title,
              type: 'tool',
              category: category.title,
            });
          }
        }
        if (toolMatches.length >= 3) break;
      }

      setSuggestions([...categoryMatches, ...toolMatches]);
    }, 150); // 150ms debounce
  }, [toolCategories]);

  // Optimized input change handler
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    handleSearchChange(value);
  }, [handleSearchChange]);

  // Optimized search functions
  const handleSearch = useCallback((item: Suggestion) => {
    if (item.type === 'category') {
      const categorySlug = slugify(item.label);
      navigate(`/categories/${categorySlug}`);
    } else if (item.type === 'tool' && item.category) {
      const categorySlug = slugify(item.category);
      const toolSlug = slugify(item.label);
      navigate(`/tools/${categorySlug}/${toolSlug}`);
    }

    setSuggestions([]);
    setQuery('');
    if (inputRef.current) {
      inputRef.current.blur();
    }
  }, [navigate]);

  const handleToolsSearch = useCallback(() => {
    if (!query.trim()) return;
    
    const lowerQuery = query.toLowerCase();
    
    // Try quick lookup from pre-computed map first
    const matchedCategory = toolMap[lowerQuery] || 
      toolCategories.find(category => 
        category.title.toLowerCase().includes(lowerQuery) ||
        category.tools.some(tool => tool.title.toLowerCase().includes(lowerQuery))
      )?.title;

    if (matchedCategory) {
      const categorySlug = slugify(matchedCategory);
      navigate(`/categories/${categorySlug}`);
    }
    
    setQuery('');
  }, [query, toolCategories, toolMap, navigate]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (suggestions.length > 0) {
        handleSearch(suggestions[0]);
      } else {
        handleToolsSearch();
      }
    }
  }, [suggestions, handleSearch, handleToolsSearch]);

  // Handle quick tag click
  const handleTagClick = useCallback((tag: string) => {
    setQuery(tag);
    
    // Use the pre-computed map for faster lookup
    const lowerTag = tag.toLowerCase();
    const matchedCategory = toolMap[lowerTag] || 
      toolCategories.find(cat => cat.title.toLowerCase().includes(lowerTag))?.title;
    
    if (matchedCategory) {
      const categorySlug = slugify(matchedCategory);
      navigate(`/categories/${categorySlug}`);
    }
  }, [toolCategories, toolMap, navigate]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <header role="banner" className="relative py-16 lg:py-24 px-4 sm:px-6 bg-gradient-to-br from-white via-purple-50/50 to-blue-50/50 dark:from-gray-900 dark:via-purple-900/20 dark:to-blue-900/20 overflow-hidden">
      {/* Optimized Background - Load after initial paint */}
      {shouldShowBackground && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <div className="absolute -top-40 -right-32 w-64 h-64 bg-purple-200/20 dark:bg-purple-500/5 rounded-full blur-xl"></div>
          <div className="absolute -bottom-40 -left-32 w-64 h-64 bg-blue-200/20 dark:bg-blue-500/5 rounded-full blur-xl"></div>
        </div>
      )}

      <div className="relative z-10 max-w-6xl mx-auto">
        {/* Header Content */}
        <div className="text-center mb-10">
          {/* Badge - Simple and fast */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-purple-200/60 dark:border-purple-700/40 rounded-full mb-6 shadow-sm">
            <Sparkles className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            <span className="text-sm font-semibold text-purple-700 dark:text-purple-300">
              {totalTools}+ AI Tools • {totalCategories}+ Categories
            </span>
          </div>

          {/* Main Heading - Critical content */}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
            Discover The Perfect
            <span className="block bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mt-2">
              AI Tool For Your Needs
            </span>
          </h1>

          {/* Subheading */}
          <h2 className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-6 font-normal">
            Explore our curated collection of <span className="font-semibold text-purple-600 dark:text-purple-400">{totalTools}+ AI tools</span> across <span className="font-semibold text-blue-600 dark:text-blue-400">{totalCategories}+ categories</span>.
          </h2>

          {/* Stats Section - Optimized */}
          <div className="mb-8">
            <h3 className="sr-only">Key Statistics</h3>
            <div className="flex flex-wrap justify-center gap-6">
              {[
                { value: `${totalTools}+`, label: 'AI Tools' },
                { value: `${totalCategories}+`, label: 'Categories' },
                { value: 'Daily', label: 'Updates' },
                { value: '100%', label: 'Free Access' }
              ].map((stat, index) => (
                <div key={index} className="text-center min-w-[100px]">
                  <div className="text-xl font-bold text-gray-900 dark:text-white">{stat.value}</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Search Section */}
        <div className="max-w-xl mx-auto">
          <h3 className="sr-only">Search AI Tools</h3>
          
          {/* Search Box */}
          <div className={`relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-xl p-1 shadow-lg border transition-all duration-200 ${
            isFocused 
              ? 'border-purple-300 dark:border-purple-600 shadow-xl' 
              : 'border-gray-200/60 dark:border-gray-700/60'
          }`}>
            <div className="flex items-center">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setTimeout(() => setIsFocused(false), 200)}
                  placeholder="Search AI tools..."
                  className="w-full bg-transparent border-none focus:outline-none text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 pl-10 pr-3 py-3 text-base"
                  aria-label="Search AI tools and categories"
                />
              </div>
              <button
                onClick={handleToolsSearch}
                className="flex items-center gap-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold px-4 py-2.5 rounded-lg transition-colors duration-200 ml-2"
                aria-label="Search button"
              >
                <Search className="w-4 h-4" />
                <span className="hidden sm:inline">Search</span>
              </button>
            </div>

            {/* Suggestions Dropdown - Virtualized if needed */}
            {suggestions.length > 0 && isFocused && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-lg z-50 overflow-hidden">
                {suggestions.slice(0, 5).map((item, idx) => (
                  <button
                    key={`${item.type}-${item.label}-${idx}`}
                    onClick={() => handleSearch(item)}
                    className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-0"
                    role="option"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {item.type === 'category' ? (
                          <TrendingUp className="w-3 h-3 text-purple-600" />
                        ) : (
                          <Zap className="w-3 h-3 text-blue-600" />
                        )}
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white text-sm">
                            {item.label}
                          </div>
                          {item.type === 'tool' && item.category && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              in {item.category}
                            </div>
                          )}
                        </div>
                      </div>
                      <ArrowRight className="w-3 h-3 text-gray-400" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Quick Search Tags - Memoized */}
          <div className="mt-4">
            <h4 className="sr-only">Popular Search Categories</h4>
            <div className="flex flex-wrap justify-center gap-2">
              {QUICK_TAGS.map((tag) => (
                <button
                  key={tag}
                  onClick={() => handleTagClick(tag)}
                  className="px-3 py-1.5 bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm border border-gray-200/60 dark:border-gray-700/60 rounded-full text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 hover:border-purple-300 dark:hover:border-purple-600 hover:text-purple-700 dark:hover:text-purple-300 transition-colors duration-200"
                  aria-label={`Search ${tag} tools`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Simplified CTA Section */}
        <div className="text-center mt-12">
          <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-200/50 dark:border-purple-700/30 rounded-xl p-6 max-w-lg mx-auto backdrop-blur-sm">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
              Ready to explore all categories?
            </h3>
            <button
              onClick={() => navigate('/categories')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
              aria-label="Explore all AI tool categories"
            >
              <Sparkles className="w-4 h-4" />
              Explore All Categories
            </button>
          </div>
        </div>
        
        {/* Hidden SEO Content - Only in production */}
        {process.env.NODE_ENV === 'production' && (
          <div className="sr-only" aria-hidden="true">
            <p>{seoDescription}</p>
          </div>
        )}
      </div>
    </header>
  );
};

export default React.memo(HomeHeader);